import { logger } from "@/utils/logger";

/**
 * Snap a VolumeViewport's camera to the nearest cardinal world axes so that
 * sibling MPR reference lines render straight (parallel to the viewport edges)
 * even when a series is acquired slightly oblique (gantry tilt / acquisition
 * noise). Without this, the `OrientationAxis.ACQUISITION` preset leaves the
 * camera rolled by the DICOM ImageOrientationPatient cosines and the whole
 * viewport — image + reference lines — renders rotated.
 *
 * Both viewport paths share this:
 *  - `GpuVolumeStrategy` (the unified path, now the default render)
 *  - legacy production `VolumeViewport` (the `?newViewport=0` escape hatch)
 *
 * Series with > ~26° off-axis tilt (dot < 0.9) are left alone — that's a
 * deliberate oblique acquisition, not acquisition noise.
 */

type Vec3 = [number, number, number];

/**
 * Whether cardinal-snapping should run for a viewport, given the active viewer
 * surfaces. Snapping straightens reference lines, but it de-aligns the camera
 * from an oblique series — and on the segmentation surface the contours are
 * placed on the series' true (possibly oblique) slice planes via each slice's
 * IPM. With a snapped (cardinal) camera those contours read as spanning several
 * slices, so one shows on multiple slices / a bump stroke edits several (the
 * coronal mask "doubling"). So we keep the true orientation on `labeling-seg`
 * and snap everywhere else (case viewer, dictation, reporting).
 */
export function shouldCardinalSnapForSurfaces(
  surfaces: readonly string[] | null | undefined,
): boolean {
  return !(surfaces?.includes("labeling-seg") ?? false);
}

const CARDINAL_AXES: Vec3[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

/**
 * Pure geometry: given a camera's viewPlaneNormal + viewUp, return the snapped
 * cardinal pair, or null when no snap should happen (already cardinal, or the
 * acquisition is deliberately oblique beyond ~26°).
 */
export function snapCameraToCardinal(
  vpn: ArrayLike<number>,
  viewUp: ArrayLike<number>,
): { vpn: Vec3; viewUp: Vec3 } | null {
  if (vpn.length !== 3 || viewUp.length !== 3) return null;
  const pickClosest = (v: ArrayLike<number>, candidates: Vec3[]): { axis: Vec3; dot: number } => {
    let best = candidates[0];
    let bestDot = -Infinity;
    for (const axis of candidates) {
      const d = v[0] * axis[0] + v[1] * axis[1] + v[2] * axis[2];
      if (d > bestDot) {
        bestDot = d;
        best = axis;
      }
    }
    return { axis: [...best] as Vec3, dot: bestDot };
  };
  const { axis: snappedVpn, dot: vpnDot } = pickClosest(vpn, CARDINAL_AXES);
  // Leave deliberately oblique acquisitions (> ~26°) alone.
  if (vpnDot < 0.9) return null;
  // viewUp candidates: the 4 cardinal axes perpendicular to snappedVpn.
  const upCandidates = CARDINAL_AXES.filter(
    (a) => a[0] * snappedVpn[0] + a[1] * snappedVpn[1] + a[2] * snappedVpn[2] === 0,
  );
  const { axis: snappedUp, dot: upDot } = pickClosest(viewUp, upCandidates);
  if (upDot < 0.9) return null;
  // Already cardinal-aligned: no-op so we don't disturb pan/zoom on reload.
  // Float tolerance because cornerstone rarely hands back exact integer
  // cardinals — VTK round-trips (0, 0, 1) into things like (0, 0, 0.9999998).
  const eq = (a: Vec3, b: ArrayLike<number>) =>
    Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6 && Math.abs(a[2] - b[2]) < 1e-6;
  if (eq(snappedVpn, vpn) && eq(snappedUp, viewUp)) return null;
  return { vpn: snappedVpn, viewUp: snappedUp };
}

/** Minimal shape we need off a live Cornerstone viewport — keeps this module
 *  free of a hard @cornerstonejs/core type dependency. */
interface SnappableViewport {
  getCamera?: () => {
    viewPlaneNormal?: ArrayLike<number>;
    viewUp?: ArrayLike<number>;
    position?: ArrayLike<number>;
    focalPoint?: ArrayLike<number>;
  } | null;
  setCamera?: (camera: Record<string, unknown>) => void;
  render?: () => void;
}

/**
 * Cornerstone's `BaseVolumeViewport.initialViewUp` — the reference frame
 * `setRotation` rebuilds viewUp from. It's declared `protected` in the published
 * typings, so it can't live on `SnappableViewport` (that interface must stay
 * structurally assignable FROM `VolumeViewport`, and a public member would
 * clash with the protected one). We reach it through this narrow local view.
 */
type ViewportWithInitialViewUp = { initialViewUp?: ArrayLike<number> };

/**
 * Read a viewport's camera, snap it to the nearest cardinal axes, re-apply, and
 * render. No-op when the camera is already cardinal-aligned or the series is
 * deliberately oblique. Safe to call right after `setVolumesForViewports` —
 * later `jumpToWorld` only moves the focal point along the normal, so the
 * snapped viewUp survives subsequent slice navigation.
 */
export function snapViewportCameraToCardinal(vp: SnappableViewport | null | undefined): void {
  try {
    const cam = vp?.getCamera?.();
    if (!cam?.viewPlaneNormal || !cam?.viewUp || !cam?.position || !cam?.focalPoint) return;
    const snapped = snapCameraToCardinal(cam.viewPlaneNormal, cam.viewUp);
    if (!snapped) return;
    const focal = cam.focalPoint;
    const pos = cam.position;
    const dist = Math.hypot(pos[0] - focal[0], pos[1] - focal[1], pos[2] - focal[2]);
    // Keep the camera on the SAME side of the slice the acquisition placed it.
    // Cornerstone's viewPlaneNormal points focalPoint -> camera, so the camera
    // sits at `focal + vpn*dist`. Using `focal - vpn*dist` flips it to the
    // opposite side, which views a (gantry-/in-plane-)tilted axial from the
    // wrong side and mirrors patient left/right. Cardinal series never reach
    // here (no-op above), so only tilted studies were affected. See
    // cameraSnap.test.ts "no L/R mirror".
    const position: Vec3 = [
      focal[0] + snapped.vpn[0] * dist,
      focal[1] + snapped.vpn[1] * dist,
      focal[2] + snapped.vpn[2] * dist,
    ];
    vp?.setCamera?.({
      ...cam,
      viewPlaneNormal: snapped.vpn,
      viewUp: snapped.viewUp,
      position,
    });
    // Move cornerstone's `initialViewUp` into the SAME frame as the snapped
    // camera. `BaseVolumeViewport.setRotation` does NOT rotate the *current*
    // viewUp — it resets viewUp to `initialViewUp` (captured from the
    // ACQUISITION orientation at viewport creation) and rotates THAT about the
    // CURRENT viewPlaneNormal. Leaving `initialViewUp` on the pre-snap tilted
    // frame means the next rotation round-trip (a resize / reset-camera
    // presentation pin) mixes the two frames and yields a viewUp that is no
    // longer perpendicular to the normal.
    //
    // That silently breaks the ruler on gantry-tilted studies: LengthTool
    // records `metadata.planeRestriction.inPlaneVector1 = viewUp`, and on
    // render `isPlaneViewable()` keeps an annotation only when
    // `dot(viewPlaneNormal, inPlaneVector1) === 0`. With a skewed viewUp that
    // dot equals the tilt (e.g. 0.309 for an 18° gantry tilt), so every
    // measurement is created and stored but filtered out of display forever —
    // the ruler looks like it does nothing, on some panes of some studies. It
    // also makes the rotation drift a little on each round-trip.
    //
    // Guarded by an `in` check so we only ever refresh a field the viewport
    // already owns (volume viewports) rather than inventing one.
    const framed = vp as ViewportWithInitialViewUp | null | undefined;
    if (framed && "initialViewUp" in framed) {
      framed.initialViewUp = snapped.viewUp;
    }
    vp?.render?.();
  } catch (err) {
    logger.debug("[cameraSnap] snap-to-cardinal failed:", err);
  }
}
